import { Check, X } from 'lucide-react';
import { useState } from 'react';

/**
 * Campo de cupom do resumo do pedido. Fica recolhido atrás do link
 * "+ Tenho um cupom" até a pessoa pedir.
 * - `cupom`: o cupom já aplicado ({ codigo, descontoCentavos }) ou null.
 * - `validar(codigo)`: chama a API e devolve { valido, motivo, codigo, descontoCentavos }.
 * O desconto exibido é só uma prévia: quem decide o valor cobrado é o
 * servidor, na hora de criar a inscrição.
 */
export default function CampoCupom({ cupom, validar, onAplicar, onRemover }) {
  const [aberto, setAberto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [erro, setErro] = useState('');

  async function aplicar() {
    const limpo = codigo.trim();
    if (!limpo || verificando) return;
    setVerificando(true);
    setErro('');
    try {
      const resultado = await validar(limpo);
      if (resultado.valido) {
        onAplicar({
          codigo: resultado.codigo ?? limpo.toUpperCase(),
          descontoCentavos: resultado.descontoCentavos ?? 0,
        });
        setCodigo('');
      } else {
        setErro(resultado.motivo ?? 'Cupom inválido ou expirado.');
      }
    } catch (err) {
      setErro(
        err?.response?.status === 429
          ? 'Muitas tentativas. Aguarde alguns minutos.'
          : 'Não foi possível verificar o cupom. Tente de novo.',
      );
    } finally {
      setVerificando(false);
    }
  }

  if (cupom) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2">
        <span className="flex min-w-0 items-center gap-2 text-xs font-bold text-emerald-800">
          <Check size={14} className="shrink-0" />
          <span className="truncate">Cupom {cupom.codigo} aplicado</span>
        </span>
        <button
          type="button"
          onClick={onRemover}
          aria-label="Remover cupom"
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white text-emerald-800 hover:bg-emerald-100"
        >
          <X size={13} />
        </button>
      </div>
    );
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="cursor-pointer self-start bg-transparent p-0 text-xs font-semibold text-[#ff2368] hover:text-[#e5195b]"
      >
        + Tenho um cupom
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <input
          type="text"
          aria-label="Cupom de desconto"
          autoFocus
          value={codigo}
          onChange={(e) => {
            setCodigo(e.target.value.toUpperCase());
            setErro('');
          }}
          onKeyDown={(e) => {
            // O campo vive dentro do <form> do checkout em uma das telas:
            // Enter aqui aplica o cupom em vez de enviar o pagamento.
            if (e.key === 'Enter') {
              e.preventDefault();
              aplicar();
            }
          }}
          placeholder="Digite o código"
          autoComplete="off"
          maxLength={20}
          className="min-w-0 flex-1 rounded-xl border border-[#eef0f4] bg-[#f6f8fb] px-3.5 py-2.5 text-[13px] font-semibold tracking-wide text-[#0f1729] placeholder:font-medium placeholder:tracking-normal placeholder:text-[#b6bdcb] focus:border-[#ff2368] focus:outline-none"
        />
        <button
          type="button"
          onClick={aplicar}
          disabled={!codigo.trim() || verificando}
          className="shrink-0 cursor-pointer rounded-xl bg-[#0f1729] px-3.5 text-[11px] font-extrabold uppercase text-white disabled:cursor-not-allowed disabled:bg-[#a3a6ad]"
        >
          {verificando ? '…' : 'Aplicar'}
        </button>
      </div>
      {erro && (
        <p className="text-[11px] font-bold text-red-500" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}
